import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  CreateCustomFieldRequest,
  CustomFieldEditRequest,
} from 'api/types/customFieldsApiTypeSchema'
import useApi from 'api/useApi'
import Permission from 'constants/Permissions'
import { useUser } from 'contexts/UserContext'
import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import { useOrgQueryKey } from 'hooks/utils/useOrgQueryKey'

import { QueryKey } from './queryKeys'

export function useGetCustomFields() {
  const { fetch_custom_fields } = useApi()
  const { enableCustomFields } = useFeatureFlags()
  const { hasPermission } = useUser()

  const customFieldsQueryKey = useOrgQueryKey(QueryKey.CustomFields)

  const userHasPermission = hasPermission(Permission.TransactionCustomFieldsRead)

  return useQuery(customFieldsQueryKey, () => fetch_custom_fields(), {
    enabled: enableCustomFields && userHasPermission,
  })
}

export function useCreateCustomFields({
  onSuccess,
  onError,
}: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}) {
  const { create_custom_field } = useApi()
  const queryClient = useQueryClient()

  return useMutation((field: CreateCustomFieldRequest) => create_custom_field(field), {
    async onSuccess() {
      onSuccess?.()
      await queryClient.invalidateQueries([QueryKey.CustomFields])
    },
    onError(error) {
      onError?.(error)
    },
  })
}

export function useUpdateCustomFields({
  onSuccess,
  onError,
}: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}) {
  const { update_custom_field } = useApi()
  const queryClient = useQueryClient()

  return useMutation((field: CustomFieldEditRequest) => update_custom_field(field.id, field), {
    async onSuccess() {
      onSuccess?.()
      await queryClient.invalidateQueries([QueryKey.CustomFields])
    },
    onError(error) {
      onError?.(error)
    },
  })
}

export function useDeleteCustomFields({
  onSuccess,
  onError,
}: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}) {
  const { delete_custom_field } = useApi()
  const queryClient = useQueryClient()

  return useMutation((fieldId: string) => delete_custom_field(fieldId), {
    async onSuccess() {
      onSuccess?.()
      await queryClient.invalidateQueries([QueryKey.CustomFields])
    },
    onError(error) {
      onError?.(error)
    },
  })
}

export function useDeleteCustomFieldApi() {
  const { delete_custom_field } = useApi()
  return (fieldId: string) => delete_custom_field(fieldId)
}
